"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeSharedKey = exports.formatX25519KeyPair = exports.decrypted = exports.encrypted = void 0;
exports.arrayBufferToHex = arrayBufferToHex;
exports.fromHex = fromHex;
const crypto_js_1 = __importDefault(require("crypto-js"));
const x25519_1 = require("@stablelib/x25519");
const AES_BLOCK_SIZE = 16; // AES block size in bytes
/**
 * AES-CFB 加密函数
 * @param data - 要加密的字符串数据
 * @returns 返回十六进制格式的加密字符串，失败时返回 null
 * @description 加密数据请求体和payload
 */
const encrypted = function (data) {
    try {
        // 从存储中获取共享密钥
        const sharedKey = localStorage.getItem('sharedKey');
        if (!sharedKey) {
            return null;
        }
        // Convert hex string to Uint8Array before creating WordArray
        const keyArray = fromHex(sharedKey);
        const keyBytes = crypto_js_1.default.lib.WordArray.create(keyArray);
        const plainText = JSON.stringify(data);
        // 初始化向量 (IV) - 每次加密都使用新的随机 IV
        const iv = crypto_js_1.default.lib.WordArray.random(AES_BLOCK_SIZE);
        // AES-CFB 模式配置
        const cfg = {
            iv: iv,
            mode: crypto_js_1.default.mode.CFB,
            padding: crypto_js_1.default.pad.NoPadding,
        };
        // 执行 AES 加密
        const encrypted = crypto_js_1.default.AES.encrypt(plainText, keyBytes, cfg);
        // 将 IV 和密文组合，以便解密时使用
        const combined = iv.concat(encrypted.ciphertext);
        return combined.toString(crypto_js_1.default.enc.Hex);
    }
    catch (error) {
        console.error('Encryption error:', error);
        return null;
    }
};
exports.encrypted = encrypted;
/**
 * AES-CFB 解密函数
 * @param data - 十六进制格式的加密字符串
 * @returns 解密后的原始数据，失败时返回 null
 * @description 解密数据请求体和payload
 */
const decrypted = function (data) {
    try {
        // 从存储中获取共享密钥
        const sharedKey = localStorage.getItem('sharedKey');
        if (!sharedKey) {
            return null;
        }
        // Convert hex string to Uint8Array before creating WordArray
        const keyArray = fromHex(sharedKey);
        const keyBytes = crypto_js_1.default.lib.WordArray.create(keyArray);
        // 将十六进制加密数据转换为 WordArray
        const combined = crypto_js_1.default.enc.Hex.parse(data);
        // 从组合数据中提取 IV 和密文
        const iv = crypto_js_1.default.lib.WordArray.create(combined.words.slice(0, AES_BLOCK_SIZE / 4), AES_BLOCK_SIZE);
        const encrypted = crypto_js_1.default.lib.WordArray.create(combined.words.slice(AES_BLOCK_SIZE / 4), combined.sigBytes - AES_BLOCK_SIZE);
        // AES-CFB 解密配置
        const cfg = {
            iv: iv,
            mode: crypto_js_1.default.mode.CFB,
            padding: crypto_js_1.default.pad.NoPadding,
        };
        // 执行解密
        const decrypted = crypto_js_1.default.AES.decrypt({ ciphertext: encrypted }, keyBytes, cfg);
        // 尝试解析解密后的数据
        const decryptedStr = decrypted.toString(crypto_js_1.default.enc.Utf8);
        return JSON.parse(decryptedStr);
    }
    catch (error) {
        console.error('Encryption error:', error);
        return null;
    }
};
exports.decrypted = decrypted;
/**
 * 生成 X25519 密钥对（Curve25519）
 * @returns 包含私钥和公钥的对象，均为 Uint8Array 格式
 *
 * X25519 是一种基于椭圆曲线的密钥交换算法：
 * - 私钥: 32 字节随机数
 * - 公钥: 由私钥通过椭圆曲线标量乘法计算得出
 *
 * 注意：前端环境中生成密钥对可能存在性能影响，建议在 Node.js 环境使用
 */
// 辅助函数：将 Uint8Array 转换为十六进制字符串
function arrayBufferToHex(arrayBuffer) {
    return Array.from(arrayBuffer)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
}
// 辅助函数：将十六进制字符串转换为 Uint8Array
function fromHex(hexString) {
    if (hexString.length % 2 !== 0) {
        throw new Error('Invalid hex string length');
    }
    const bytes = [];
    for (let c = 0; c < hexString.length; c += 2) {
        const byte = parseInt(hexString.substr(c, 2), 16);
        if (isNaN(byte)) {
            throw new Error('Invalid hex string');
        }
        bytes.push(byte);
    }
    return new Uint8Array(bytes);
}
const formatX25519KeyPair = () => {
    // 生成密钥对（自动包含安全随机数生成）
    const keyPair = (0, x25519_1.generateKeyPair)();
    return {
        privateKey: keyPair.secretKey, // 32 字节私钥
        publicKey: keyPair.publicKey, //32 字节公钥
    };
};
exports.formatX25519KeyPair = formatX25519KeyPair;
// 生成共享密钥的函数（和serverPubKey匹配上）
const computeSharedKey = (privateKey, serverPubKey) => {
    const publicKey = fromHex(serverPubKey);
    return (0, x25519_1.sharedKey)(privateKey, publicKey);
};
exports.computeSharedKey = computeSharedKey;
